import React, { useState } from "react";
import {
  ShieldCheck,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Copy,
  Download,
  FileCode2,
  FlaskConical,
  Building2,
  GraduationCap,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import { Reveal, PageTransition } from "@/components/Reveal";
import { PageHero, CTABand } from "@/components/CTA";
import { MoreTools } from "@/components/MoreTools";
import { Seo } from "@/components/Seo";

const faqs = [
  {
    q: "What is a payer-specific synthetic 837 generator?",
    a: "It creates complete, well-formed X12 837 EDI claim files populated entirely with synthetic data — fake patients, checksum-valid fake NPIs, and fictional charges — structured for a specific payer's envelope. Use it to test intake pipelines, clearinghouse onboarding, and adjudication logic without touching a single real claim.",
  },
  {
    q: "What does 'payer-specific' mean in the generated file?",
    a: "Each payer selection sets the receiver envelope identifiers the way that payer's companion guide expects: the ISA/GS receiver IDs and the payer name in the 2010BB NM1 loop. Optional toggles add commonly payer-mandated segments like prior authorization (REF*G1) and rendering provider loops, so you can rehearse payer edits before sending real files.",
  },
  {
    q: "Which transaction types are supported?",
    a: "All three healthcare claim transactions: 837P (professional, version 005010X222A1), 837I (institutional, 005010X223A2), and 837D (dental, 005010X224A2). Service lines use SV1 with HCPCS/CPT, SV2 with revenue codes, or SV3 with CDT codes respectively.",
  },
  {
    q: "Are the claims in the generated file real?",
    a: "No. Every patient is synthetic, every NPI passes the CMS Luhn checksum but was randomly generated, and all charges, dates, and control numbers are fictional. The files are structurally valid 837s — they will parse in any EDI validator — but contain zero real data.",
  },
  {
    q: "Can I submit these files to a clearinghouse or payer?",
    a: "Only to their test endpoints. Never submit synthetic claims to production — fabricated claims are fraud. These files are for test environments: clearinghouse certification (most clearinghouses like Availity, Waystar, and Change Healthcare offer test modes), EDI parser development, and QA.",
  },
  {
    q: "How should I use synthetic 837 files in testing?",
    a: "Feed them through your intake pipeline to validate ISA/GS envelope handling, loop parsing (billing provider, subscriber, claim, service lines), balancing (CLM totals vs. line sums), and segment counting (SE01). Then run them through your adjudication or RCM system to rehearse 999/277CA acknowledgments and rejection handling.",
  },
  {
    q: "Is this 837 test file generator free?",
    a: "Yes — 100% free, no signup, no API key. Everything is generated in your browser; nothing is sent to or stored on any server. Generate up to 10 claims per file and download as a .edi file in one click.",
  },
  {
    q: "Where do the fake NPIs and patients come from?",
    a: "From the same generators behind our standalone tools: NPIs use the CMS Luhn construction with the 80840 prefix, and patients use never-issued 9xx SSN ranges and fictional 555 phone numbers. You can also use our Fake NPI Generator and Fake Patient Generator tools directly for custom test data.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const softwareJsonLd = {
  "@context": "https://schema.org",
  "@type": ["WebApplication", "SoftwareApplication"],
  name: "Hewto.ai Payer-Specific Synthetic 837 Generator",
  applicationCategory: "DeveloperApplication",
  operatingSystem: "Web",
  browserRequirements: "Requires JavaScript",
  description:
    "Free payer-specific synthetic 837 generator — creates complete X12 837P/837I/837D EDI test claim files with synthetic patients, checksum-valid fake NPIs, and payer-specific envelopes. Download .edi files for clearinghouse certification and pipeline testing.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
};

// ---------- Data ----------
const PAYERS = [
  { id: "medicare", name: "Medicare (Part B MAC)", receiverId: "CMS", payerName: "MEDICARE" },
  { id: "medicaid", name: "Medicaid (State MMIS)", receiverId: "MEDICAID", payerName: "MEDICAID" },
  { id: "uhc", name: "UnitedHealthcare", receiverId: "87726", payerName: "UNITEDHEALTHCARE" },
  { id: "aetna", name: "Aetna", receiverId: "60054", payerName: "AETNA" },
  { id: "cigna", name: "Cigna", receiverId: "62308", payerName: "CIGNA" },
  { id: "humana", name: "Humana", receiverId: "61101", payerName: "HUMANA" },
  { id: "anthem", name: "Anthem Blue Cross", receiverId: "00310", payerName: "ANTHEM BLUE CROSS" },
];

const TXN_TYPES = [
  { id: "837P", label: "837P — Professional", version: "005010X222A1", gs: "HP" },
  { id: "837I", label: "837I — Institutional", version: "005010X223A2", gs: "HI" },
  { id: "837D", label: "837D — Dental", version: "005010X224A2", gs: "HN" },
];

const FIRST_MALE = ["James", "Robert", "Michael", "William", "David", "Joseph", "Thomas", "Daniel"];
const FIRST_FEMALE = ["Mary", "Patricia", "Jennifer", "Linda", "Elizabeth", "Susan", "Sarah", "Karen"];
const LAST_NAMES = ["Smith", "Johnson", "Williams", "Brown", "Garcia", "Miller", "Davis", "Martinez"];
const STREETS = ["Main St", "Oak Ave", "Maple Dr", "Cedar Ln", "Pine St", "Park Blvd"];
const CITIES = [
  { city: "SPRINGFIELD", state: "IL", zip: "62701" },
  { city: "AUSTIN", state: "TX", zip: "73301" },
  { city: "COLUMBUS", state: "OH", zip: "43004" },
  { city: "PHOENIX", state: "AZ", zip: "85001" },
  { city: "DENVER", state: "CO", zip: "80201" },
];
const ICD10 = ["R5021", "E119", "I10", "M5450", "J069", "K2130", "R05", "F419", "N390", "J029"];
const CPT = ["99213", "99214", "99203", "97110", "80053", "93000", "90471", "99232"];
const REV_CPT = [
  { rev: "0450", code: "99285" },
  { rev: "0250", code: "J1885" },
  { rev: "0300", code: "80053" },
  { rev: "0320", code: "71045" },
];
const CDT = ["D0120", "D0150", "D0274", "D1110", "D2391", "D7140"];

const rand = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randInt = (min, max) => min + Math.floor(Math.random() * (max - min + 1));
const pad = (n, l) => String(n).padStart(l, "0");
const padRight = (s, l) => String(s).padEnd(l, " ").slice(0, l);

const computeCheckDigit = (nine) => {
  const payload = "80840" + nine;
  let sum = 0;
  for (let i = 0; i < payload.length; i++) {
    let d = Number(payload[payload.length - 1 - i]);
    if (i % 2 === 0) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return String((10 - (sum % 10)) % 10);
};

const fakeNpi = () => {
  let nine = String(randInt(1, 2));
  while (nine.length < 9) nine += String(randInt(0, 9));
  return nine + computeCheckDigit(nine);
};

const now = new Date();
const isaDate = `${String(now.getFullYear()).slice(2)}${pad(now.getMonth() + 1, 2)}${pad(now.getDate(), 2)}`;
const isaTime = `${pad(now.getHours(), 2)}${pad(now.getMinutes(), 2)}`;
const gsDate = `${now.getFullYear()}${pad(now.getMonth() + 1, 2)}${pad(now.getDate(), 2)}`;
const ccyy = (d) =>
  `${d.getFullYear()}${pad(d.getMonth() + 1, 2)}${pad(d.getDate(), 2)}`;

const makePatient = () => {
  const female = Math.random() < 0.5;
  const loc = rand(CITIES);
  const dob = new Date(randInt(1940, 2005), randInt(0, 11), randInt(1, 28));
  return {
    first: female ? rand(FIRST_FEMALE) : rand(FIRST_MALE),
    last: rand(LAST_NAMES),
    gender: female ? "F" : "M",
    dob: ccyy(dob),
    memberId: `MB${pad(randInt(0, 999999999), 9)}`,
    group: `GRP${pad(randInt(0, 999999), 6)}`,
    street: `${randInt(100, 9999)} ${rand(STREETS)}`,
    ...loc,
  };
};

const buildEdi = ({ txn, payer, claimCount, priorAuth, rendering }) => {
  const submitter = "HEWTOTEST";
  const isaCtrl = pad(randInt(1, 999999999), 9);
  const stCtrl = "0001";
  const seg = []; // ST..SE body
  const t = TXN_TYPES.find((x) => x.id === txn);

  seg.push(`ST*837*${stCtrl}*${t.version}`);
  seg.push(`BHT*0019*00*${pad(randInt(1, 99999), 6)}*${gsDate}*${isaTime}*CH`);
  seg.push(`NM1*41*2*HEWTO TEST BILLING*****46*${submitter}`);
  seg.push(`PER*IC*EDI TEST CONTACT*TE*5555550100`);
  seg.push(`NM1*40*2*${payer.payerName}*****46*${payer.receiverId}`);

  let claimTotal = 0;
  for (let c = 0; c < claimCount; c++) {
    const patient = makePatient();
    const billingNpi = fakeNpi();
    const renderNpi = fakeNpi();
    const svcDate = new Date(now.getTime() - randInt(1, 30) * 86400000);
    const svc = ccyy(svcDate);
    const lineCount = txn === "837D" ? 1 : randInt(1, 3);

    seg.push(`HL*${1 + c * 2}**20*1`);
    seg.push(`NM1*85*1*${rand(LAST_NAMES).toUpperCase()}*${rand(FIRST_MALE).toUpperCase()}****XX*${billingNpi}`);
    seg.push(`N3*${patient.street}`);
    seg.push(`N4*${patient.city}*${patient.state}*${patient.zip}`);
    seg.push(`REF*EI*${pad(randInt(100000000, 999999999), 9)}`);
    seg.push(`HL*${2 + c * 2}*${1 + c * 2}*22*0`);
    seg.push(`SBR*P*18*${patient.group}******CI`);
    seg.push(`NM1*IL*1*${patient.last.toUpperCase()}*${patient.first.toUpperCase()}****MI*${patient.memberId}`);
    seg.push(`N3*${patient.street}`);
    seg.push(`N4*${patient.city}*${patient.state}*${patient.zip}`);
    seg.push(`DMG*D8*${patient.dob}*${patient.gender}`);
    seg.push(`NM1*PR*2*${payer.payerName}*****PI*${payer.receiverId}`);

    // claim
    let lines = [];
    let clmAmount = 0;
    for (let l = 1; l <= lineCount; l++) {
      const amt = randInt(50, 800);
      clmAmount += amt;
      if (txn === "837P") {
        lines.push([`LX*${l}`, `SV1*HC:${rand(CPT)}*${amt}*UN*1***1`, `DTP*472*D8*${svc}`]);
      } else if (txn === "837I") {
        const r = rand(REV_CPT);
        lines.push([`LX*${l}`, `SV2*${r.rev}*HC:${r.code}*${amt}*UN*1`, `DTP*472*D8*${svc}`]);
      } else {
        lines.push([`LX*${l}`, `SV3*AD:${rand(CDT)}*${amt}**1`, `DTP*472*D8*${svc}`]);
      }
    }
    const billType = txn === "837I" ? "131" : "11";
    seg.push(`CLM*SYNTH${pad(c + 1, 5)}*${clmAmount}***${billType}:B:1*Y*A*Y*I`);
    if (priorAuth) seg.push(`REF*G1*PA${pad(randInt(100000, 999999), 6)}`);
    seg.push(`HI*ABK:${rand(ICD10)}`);
    lines.forEach((l) => l.forEach((s) => seg.push(s)));
    if (rendering) seg.push(`NM1*82*1*${rand(LAST_NAMES).toUpperCase()}*${rand(FIRST_FEMALE).toUpperCase()}****XX*${renderNpi}`);
    claimTotal += clmAmount;
  }

  const seCount = seg.length + 1; // + SE itself
  seg.push(`SE*${seCount}*${stCtrl}`);

  const isa = [
    `ISA*00*          *00*          *ZZ*${padRight(submitter, 15)}*ZZ*${padRight(payer.receiverId, 15)}*${isaDate}*${isaTime}*^*00501*${isaCtrl}*0*T*:`,
    `GS*${t.gs}*${submitter}*${payer.receiverId}*${gsDate}*${isaTime}*1*X*${t.version}`,
    ...seg,
    `GE*1*1`,
    `IEA*1*${isaCtrl}`,
  ];
  return { edi: isa.join("~\n") + "~", claims: claimCount, segments: seCount, total: claimTotal };
};

const COUNT_OPTIONS = [1, 3, 5, 10];

const Synthetic837 = () => {
  const [txn, setTxn] = useState("837P");
  const [payerId, setPayerId] = useState("uhc");
  const [claimCount, setClaimCount] = useState(3);
  const [priorAuth, setPriorAuth] = useState(false);
  const [rendering, setRendering] = useState(true);
  const [result, setResult] = useState(null);

  const payer = PAYERS.find((p) => p.id === payerId);

  const runGenerate = () => {
    const out = buildEdi({ txn, payer, claimCount, priorAuth, rendering });
    setResult(out);
    toast.success(`Generated ${txn} file with ${out.claims} synthetic claim${out.claims === 1 ? "" : "s"}`);
  };

  const copyEdi = () => {
    navigator.clipboard.writeText(result.edi);
    toast.success("EDI file copied to clipboard");
  };

  const downloadEdi = () => {
    const blob = new Blob([result.edi], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `synthetic-${txn.toLowerCase()}-${payer.id}-${gsDate}.edi`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PageTransition>
      <Seo
        title="Payer-Specific Synthetic 837 Generator — Free 837 Test Files | Hewto.ai"
        description="Free payer-specific synthetic 837 generator. Create complete X12 837P, 837I, and 837D EDI test claim files with synthetic patients, checksum-valid fake NPIs, and payer-specific envelopes (Medicare, Medicaid, UHC, Aetna, Cigna, Humana, Anthem). Download .edi files for clearinghouse certification and pipeline testing. No signup."
        keywords="837 test file generator, synthetic 837, synthetic 837 generator, payer specific 837, edi 837 test data, 837p test file, 837i test file, 837d test file, fake 837 file, sample 837 file, edi test file generator, healthcare edi testing, 837 companion guide testing, clearinghouse test claims, hipaa 837 test data, free 837 generator, x12 837 sample, 837 edi example, test edi claims, synthetic claims data, 837p sample file, edi 837 generator"
        canonical="https://www.hewto.ai/tools/payer-specific-synthetic-837"
        jsonLd={[faqJsonLd, softwareJsonLd]}
      />

      <PageHero
        eyebrow="Free tool · Payer-Specific Synthetic 837"
        title="Synthetic 837 Test File Generator"
        subtitle="Generate complete, structurally valid X12 837P, 837I, and 837D claim files with payer-specific envelopes — synthetic patients, checksum-valid fake NPIs, and fictional charges, ready for clearinghouse certification and pipeline testing. Download as .edi in one click. Free, no signup, nothing stored."
      />

      <section className="mx-auto max-w-6xl px-5 pb-20 sm:px-8">
        <Reveal>
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg shadow-brand/5 sm:p-8">
            <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="syn837-disclaimer">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <p className="text-sm leading-relaxed text-amber-800">
                <span className="font-semibold">Test environments only.</span> Every claim is
                synthetic — never submit generated files to production payers or any
                HIPAA-covered transaction.
              </p>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="syn837-txn" className="text-sm font-semibold text-slate-700">
                  Transaction type
                </label>
                <select
                  id="syn837-txn"
                  data-testid="syn837-txn"
                  value={txn}
                  onChange={(e) => setTxn(e.target.value)}
                  className="mt-2 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:border-brand focus:outline-none"
                >
                  {TXN_TYPES.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label} ({t.version})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="syn837-payer" className="text-sm font-semibold text-slate-700">
                  Payer envelope
                </label>
                <select
                  id="syn837-payer"
                  data-testid="syn837-payer"
                  value={payerId}
                  onChange={(e) => setPayerId(e.target.value)}
                  className="mt-2 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:border-brand focus:outline-none"
                >
                  {PAYERS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} · ID {p.receiverId}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700">Claims per file</label>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="syn837-count-group">
                  {COUNT_OPTIONS.map((c) => (
                    <button
                      key={c}
                      data-testid={`syn837-count-${c}`}
                      onClick={() => setClaimCount(c)}
                      className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                        claimCount === c
                          ? "bg-brand text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700">Optional segments</label>
                <div className="mt-2 flex flex-wrap gap-4">
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      data-testid="syn837-priorauth"
                      checked={priorAuth}
                      onChange={(e) => setPriorAuth(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand"
                    />
                    Prior authorization (REF*G1)
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      data-testid="syn837-rendering"
                      checked={rendering}
                      onChange={(e) => setRendering(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand"
                    />
                    Rendering provider (NM1*82)
                  </label>
                </div>
              </div>
            </div>

            <Button
              data-testid="syn837-generate-btn"
              onClick={runGenerate}
              className="mt-6 h-12 w-full rounded-xl bg-brand px-6 text-base font-semibold text-white hover:bg-brand-600"
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Generate synthetic {txn} file
            </Button>
            <p className="mt-2 text-xs text-slate-500">
              Generated in your browser — nothing is sent to or stored on our servers.
            </p>

            {result && (
              <div className="mt-6" data-testid="syn837-results">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-slate-700" data-testid="syn837-stats">
                    {result.claims} claim{result.claims === 1 ? "" : "s"} · {result.segments} ST-SE
                    segments · ${result.total.toLocaleString()} total charges
                  </p>
                  <div className="flex gap-2">
                    <Button data-testid="syn837-copy-btn" variant="outline" onClick={copyEdi} className="rounded-full">
                      <Copy className="mr-2 h-4 w-4" /> Copy EDI
                    </Button>
                    <Button data-testid="syn837-download-btn" variant="outline" onClick={downloadEdi} className="rounded-full">
                      <Download className="mr-2 h-4 w-4" /> Download .edi
                    </Button>
                  </div>
                </div>
                <pre
                  data-testid="syn837-preview"
                  className="mt-4 max-h-[480px] overflow-auto rounded-2xl border border-slate-200 bg-slate-950 p-5 font-mono text-xs leading-relaxed text-slate-200"
                >
                  {result.edi}
                </pre>
              </div>
            )}
          </div>
        </Reveal>
      </section>

      {/* WHAT'S INSIDE */}
      <section className="bg-slate-50 py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-12 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <div>
              <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
                Under the hood
              </span>
              <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                A complete, balanced 837 — not a snippet
              </h2>
              <p className="mt-4 leading-relaxed text-slate-600">
                Each file is a full production-shaped transmission, so your parsers, validators,
                and balancing logic see exactly what a real clearinghouse file looks like:
              </p>
              <ul className="mt-6 space-y-3 text-sm text-slate-700">
                {[
                  "ISA/GS/GE/IEA envelopes with payer-specific receiver IDs and correct fixed-width ISA padding",
                  "Versioned ST/SE per transaction: X222A1 (837P), X223A2 (837I), X224A2 (837D)",
                  "HL hierarchy: billing provider → subscriber → claim, with NM1*85/IL/PR loops",
                  "CLM totals always equal the sum of SV1/SV2/SV3 line charges — balancing passes",
                  "Synthetic patients with never-issued identifiers; NPIs pass the CMS Luhn checksum",
                  "Optional payer-mandated segments: prior authorization (REF*G1) and rendering provider (NM1*82)",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-slate-200 bg-slate-950 p-6 shadow-lg">
              <pre className="whitespace-pre text-xs leading-relaxed text-slate-200">
{`ISA*00*          *00*          *ZZ*HEWTOTEST
   *ZZ*87726         *250929*1200*^*00501
   *000000001*0*T*:~
GS*HP*HEWTOTEST*87726*20250929*1200*1*X
   *005010X222A1~
ST*837*0001*005010X222A1~
BHT*0019*00*012345*20250929*1200*CH~
...
CLM*SYNTH00001*500***11:B:1*Y*A*Y*I~
HI*ABK:E119~
LX*1~
SV1*HC:99213*150*UN*1***1~
DTP*472*D8*20250915~
...
SE*24*0001~
GE*1*1~
IEA*1*000000001~`}
              </pre>
            </div>
            <p className="mt-4 text-sm text-slate-500">
              Pair with the{" "}
              <a href="/tools/fake-npi-generator" className="font-semibold text-brand hover:underline">
                Fake NPI Generator
              </a>{" "}
              and{" "}
              <a href="/tools/fake-patient-generator" className="font-semibold text-brand hover:underline">
                Fake Patient Generator
              </a>{" "}
              for fully custom test data, or convert real paper claims to 837 with{" "}
              <a href="/paper-to-edi-service" className="font-semibold text-brand hover:underline">
                Hewto.ai's Paper to EDI service
              </a>
              .
            </p>
          </Reveal>
        </div>
      </section>

      {/* USE CASES */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200/40 bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-700">
              <Sparkles className="h-3.5 w-3.5" /> Why synthetic 837s
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Production-shaped test claims, zero PHI
            </h2>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {[
            {
              icon: Building2,
              title: "Clearinghouse certification",
              desc: "Rehearse Availity, Waystar, or Change Healthcare test-mode submissions with payer-shaped envelopes before your first real file.",
            },
            {
              icon: FlaskConical,
              title: "Pipeline & parser QA",
              desc: "Exercise ISA/GS handling, loop parsing, claim balancing, and SE segment counting against structurally valid files — 837P, I, and D.",
            },
            {
              icon: GraduationCap,
              title: "Training & demos",
              desc: "Teach EDI analysts and demo RCM workflows on realistic 837 files with zero protected health information anywhere in the loop.",
            },
          ].map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand">
                  <f.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 text-lg font-semibold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="mx-auto mt-10 flex max-w-3xl items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
            <p className="text-sm leading-relaxed text-slate-600">
              Envelope IDs are the commonly published payer identifiers for each payer — always
              confirm against the payer's current companion guide before certification testing.
              Generated files are synthetic and carry no real member, provider, or claim data.
            </p>
          </div>
        </Reveal>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-5 py-24 sm:px-8">
        <Reveal>
          <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
            Synthetic 837 FAQ
          </span>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Common questions about 837 test files
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="mt-8" data-testid="syn837-faq">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
                <AccordionTrigger
                  data-testid={`syn837-faq-trigger-${i}`}
                  className="text-left text-base font-semibold text-slate-900 hover:no-underline"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      <MoreTools current="/tools/payer-specific-synthetic-837" />

      <CTABand />
    </PageTransition>
  );
};

export default Synthetic837;
